import React, { useEffect, useMemo, useRef, useState } from "react";
import "../JobPosting/jobs.scss";
import "./teamAssignment.scss";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import {
  fetchJobAssignmentSettings,
  updateJobAssignmentSettings,
  fetchJobAccessList,
  addJobAccessByCompanies,
  updateJobAccessMember,
  removeJobAccessMember,
  fetchJobRoundRobinPool,
  setJobRoundRobinPool,
} from "../../Redux/ActionCreators/jobsAction";
import { loadCompanyMembers } from "../../Redux/ActionCreators/chatAction";
import { getCompanies } from "../../Redux/ActionCreators/settingAction";
import InfoTip from "../../utils/InfoTip";
import { useJobSettingsAccess } from "../JobPosting/JobSettingsAccessContext";

export const TEAM_ASSIGNMENT_TIPS = {
  teamAccess:
    "When ON, only admins and the users added to this job team can see this job and its candidates.\n\nWhen OFF, everyone in the Team can see the job.",
  fullAccess:
    "Admins and managers always have full candidate access. For recruiters, full candidate access lets them see every candidate on this job, change status, assign stages and assign / reassign / remove who a candidate is assigned to.\n\nWithout it, when round-robin is on, the member can only work on candidates assigned to them.",
  roundRobin:
    "When ON, every new candidate is automatically assigned to the next person in the pool, in order.\n\nTurning it on also assigns the existing unassigned candidates once.\n\nMembers without full access can only open, change status and assign stages for their own candidates.",
  pool:
    "Only members in the pool receive candidates. Tick a member to add them and use the arrows to set the rotation order.",
};

// "Who can do what" matrix: Y = allowed, P = own candidates only, N = not allowed
// "Full candidate access" = recruiters with the checkbox ticked; "Assigned only" = recruiters without it
const PERMISSION_COLUMNS = ["Admin", "Manager", "Full candidate access", "Assigned only", "Not on team"];
const PERMISSION_ROWS = [
  { label: "See the job", values: ["Y", "Y", "Y", "Y", "N"] },
  { label: "See candidates", values: ["Y", "Y", "Y", "P", "N"] },
  { label: "Change status & stage", values: ["Y", "Y", "Y", "P", "N"] },
  { label: "Assign & reassign", values: ["Y", "Y", "Y", "N", "N"] },
];
const PERMISSION_MARK = {
  Y: { cls: "ta-yes", text: "✓" },
  P: { cls: "ta-part", text: "◐" },
  N: { cls: "ta-no", text: "✕" },
};

// Admins and managers always have full candidate access, so the checkbox is only shown for recruiters.
// Backend may send the role as a code or a display name ("Hiring Manager"), so normalise to UPPER_SNAKE.
const FULL_ACCESS_ROLES = ["ADMIN", "SUPER_ADMIN", "COMPANY_ADMIN", "MANAGER", "HIRING_MANAGER"];

const normaliseRole = (raw) => String(raw || "").trim().toUpperCase().replace(/\s+/g, "_");

const hasFixedFullAccess = (roleKey) => FULL_ACCESS_ROLES.includes(roleKey);

// Lower number = higher privilege; used when a member has more than one role.
const rolePriority = (key) => {
  if (key.includes("ADMIN")) return 0;
  if (key.includes("MANAGER")) return 1;
  if (key === "RECRUITER") return 2;
  return 3;
};

// A company member's `id` is the job ACL's `company_user_id`. Codes arrive in mixed case
// ("recruiter" / "RECRUITER") and a member can hold several roles, so pick the most privileged one.
const roleOf = (member) => {
  const roles = (Array.isArray(member?.roles) ? member.roles : [])
    .map((r) => ({ key: normaliseRole(r.code || r.name), name: r.name || r.code || "" }))
    .filter((r) => r.key);
  if (!roles.length) return { key: "", label: "" };
  const best = roles.reduce((a, b) => (rolePriority(b.key) < rolePriority(a.key) ? b : a));
  // Prefer the display name, but fall back to a readable code when the name looks like a code ("ADMIN1").
  const label = /^[A-Z0-9_]+$/.test(best.name)
    ? best.key.toLowerCase().split("_").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ")
    : best.name;
  return { key: best.key, label };
};

const roleTone = (roleKey) => {
  if (roleKey.includes("ADMIN")) return "admin";
  if (roleKey.includes("MANAGER")) return "manager";
  if (roleKey === "RECRUITER") return "recruiter";
  return "default";
};

const RoleBadge = ({ roleKey, label }) =>
  label
    ? <span className={`ta-role-badge ta-role-${roleTone(roleKey)}`}>{label}</span>
    : <span className="ta-role-badge ta-role-none" title="No role assigned in this Team">No role</span>;

const addSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 8,
    fontSize: 14,
    fontFamily: '"inter", sans-serif',
    borderColor: state.isFocused ? "#f75891" : "#e4e9f0",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(247, 88, 145, 0.12)" : "none",
    "&:hover": { borderColor: state.isFocused ? "#f75891" : "#f9a8d4" },
  }),
  placeholder: (base) => ({ ...base, color: "#8a9199" }),
  singleValue: (base) => ({ ...base, color: "#111928" }),
  multiValue: (base) => ({ ...base, backgroundColor: "#fce7f1", borderRadius: 6 }),
  multiValueLabel: (base) => ({ ...base, color: "#111928", fontSize: 13 }),
  multiValueRemove: (base) => ({ ...base, color: "#f75891", "&:hover": { backgroundColor: "#f9a8d4", color: "#fff" } }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, borderRadius: 8, overflow: "hidden", boxShadow: "0 8px 24px rgba(17, 25, 40, 0.12)" }),
  option: (base, state) => ({
    ...base,
    padding: "8px 12px",
    cursor: "pointer",
    fontFamily: '"inter", sans-serif',
    backgroundColor: state.isSelected ? "#fce7f1" : state.isFocused ? "#fff7fb" : "#fff",
    color: "#111928",
  }),
};

const initialsOf = (name) => {
  const parts = String(name || "?").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const Icon = {
  lock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
  ),
  globe: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></svg>
  ),
  trash: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" /></svg>
  ),
  up: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><path d="M12 19V5M5 12l7-7 7 7" /></svg>
  ),
  down: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><path d="M12 5v14M5 12l7 7 7-7" /></svg>
  ),
  chevron: (
    <svg className="ta-chev" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M6 9l6 6 6-6" /></svg>
  ),
};

// Same pink toggle as the other job settings pages
const Switch = ({ checked, onChange, label, disabled }) => (
  <label className={`toggle-switch toggle-pink${disabled ? " ta-switch-disabled" : ""}`} title={label}>
    <input type="checkbox" checked={checked} disabled={disabled} aria-label={label} onChange={(e) => onChange(e.target.checked)} />
    <span className="toggle-slider" />
  </label>
);

const TeamAssignment = ({ setType, setSnackbar }) => {
  const { jobId } = useParams();
  const dispatch = useDispatch();
  const { readOnly } = useJobSettingsAccess();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyMemberId, setBusyMemberId] = useState(null);
  const [error, setError] = useState("");

  const [teamAccess, setTeamAccess] = useState(false);
  const [roundRobin, setRoundRobin] = useState(false);
  const [team, setTeam] = useState([]); // [{ id, name, email, role_key, role_name, full_candidate_access }]
  const [rrPoolIds, setRrPoolIds] = useState([]);
  const [companyMembers, setCompanyMembers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companiesToAdd, setCompaniesToAdd] = useState([]); // company ids
  const [addingCompanies, setAddingCompanies] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState(null); // member
  const [justAddedId, setJustAddedId] = useState(null);
  const [permOpen, setPermOpen] = useState(false);
  const [dragOverId, setDragOverId] = useState(null);
  const dragIdRef = useRef(null);
  const rootRef = useRef(null);
  const [barPos, setBarPos] = useState(null); // { left, width } of the page, for the fixed save bar

  // Last saved values of the settings that go through the Save button (team members save immediately).
  const [saved, setSaved] = useState({ teamAccess: false, roundRobin: false, rrPoolIds: [] });

  const notify = (message, severity = "error") => setSnackbar?.({ open: true, message, severity });

  const memberInfo = (companyUserId, members = companyMembers) => {
    const m = members.find((c) => c.id === companyUserId);
    const role = roleOf(m);
    return {
      id: companyUserId,
      name: m?.name || m?.email || `${String(companyUserId).slice(0, 8)}…`,
      email: m?.email || "",
      company_name: m?.company?.name || "",
      role_key: role.key,
      role_name: role.label,
    };
  };

  const toTeam = (aclData, members = companyMembers) =>
    (Array.isArray(aclData) ? aclData : [])
      .filter((a) => a.is_active !== false)
      .map((a) => {
        const info = memberInfo(a.company_user_id, members);
        return {
          ...info,
          full_candidate_access: hasFixedFullAccess(info.role_key) || !!a.full_candidate_access,
        };
      });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      // Company members are still needed to show name / email / role for each ACL row
      const [settingsRes, aclRes, poolRes, membersRes, companiesRes] = await Promise.all([
        dispatch(fetchJobAssignmentSettings(jobId)),
        dispatch(fetchJobAccessList(jobId)),
        dispatch(fetchJobRoundRobinPool(jobId)),
        dispatch(loadCompanyMembers()),
        dispatch(getCompanies(200, 1)),
      ]);
      const members = (membersRes?.data || []).filter((m) => m.id);
      setCompanyMembers(members);
      setCompanies((companiesRes?.payload?.data || []).filter((c) => c.id && c.is_active !== false));

      if (!settingsRes.success) setError(settingsRes.message);
      const settings = settingsRes.data || {};
      const access = !!settings.team_access_enabled;
      const rr = !!settings.round_robin_enabled;
      setTeamAccess(access);
      setRoundRobin(rr);

      setTeam(toTeam(aclRes.data, members));

      const poolIds = (Array.isArray(poolRes.data) ? poolRes.data : [])
        .slice()
        .sort((a, b) => (a.rr_order || 0) - (b.rr_order || 0))
        .map((p) => p.company_user_id);
      setRrPoolIds(poolIds);
      setSaved({ teamAccess: access, roundRobin: rr, rrPoolIds: poolIds });
      setLoading(false);
    };
    if (jobId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const companyOptions = useMemo(() => companies.map((c) => ({
    value: c.id,
    company: c,
    // Searched by react-select's default filter
    label: [c.name, c.code].filter(Boolean).join(" "),
  })), [companies]);

  const orderedRrMembers = useMemo(() => [
    ...rrPoolIds.map((id) => team.find((t) => t.id === id)).filter(Boolean),
    ...team.filter((t) => !rrPoolIds.includes(t.id)),
  ], [team, rrPoolIds]);

  const isDirty =
    teamAccess !== saved.teamAccess ||
    roundRobin !== saved.roundRobin ||
    rrPoolIds.join(",") !== saved.rrPoolIds.join(",");

  // The save bar is fixed to the bottom of the screen; line it up with this page's column
  // so it doesn't cover the sidebar (whose width changes when collapsed).
  useEffect(() => {
    const el = rootRef.current;
    if (!isDirty || !el) return undefined;
    const update = () => {
      const r = el.getBoundingClientRect();
      setBarPos({ left: r.left, width: r.width });
    };
    update();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    ro?.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [isDirty]);

  // ── Team (ACL) — these hit the API immediately ────────────────────────────
  // Backend adds every user of the selected companies; reload the ACL to pick them up.
  const handleAddCompanies = async () => {
    if (readOnly || !companiesToAdd.length) return;
    setAddingCompanies(true);
    const res = await dispatch(addJobAccessByCompanies(jobId, companiesToAdd, false));
    if (res.success) {
      setCompaniesToAdd([]);
      const aclRes = await dispatch(fetchJobAccessList(jobId));
      if (aclRes.success) {
        setTeam(toTeam(aclRes.data));
      } else {
        notify(aclRes.message);
      }
      notify(res.message || "Team users added to the job team", "success");
    } else {
      notify(res.message);
    }
    setAddingCompanies(false);
  };

  const handleToggleFullAccess = async (member, checked) => {
    if (readOnly || hasFixedFullAccess(member.role_key)) return;
    setBusyMemberId(member.id);
    const res = await dispatch(updateJobAccessMember(jobId, member.id, checked));
    if (res.success) {
      setTeam((prev) => prev.map((t) => (t.id === member.id ? { ...t, full_candidate_access: checked } : t)));
    } else {
      notify(res.message);
    }
    setBusyMemberId(null);
  };

  const handleRemoveMember = async () => {
    const member = removeConfirm;
    setRemoveConfirm(null);
    if (readOnly || !member) return;
    setBusyMemberId(member.id);
    const res = await dispatch(removeJobAccessMember(jobId, member.id));
    if (res.success) {
      setTeam((prev) => prev.filter((t) => t.id !== member.id));
      setRrPoolIds((prev) => prev.filter((id) => id !== member.id));
      // The server drops them from the pool too, so this isn't an unsaved change
      setSaved((prev) => ({ ...prev, rrPoolIds: prev.rrPoolIds.filter((id) => id !== member.id) }));
    } else {
      notify(res.message);
    }
    setBusyMemberId(null);
  };

  // ── Round-robin pool (saved with the Save button) ─────────────────────────
  const toggleRrMember = (id, checked) => {
    if (readOnly) return;
    setError("");
    setRrPoolIds((prev) => (checked ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((x) => x !== id)));
  };

  const moveRr = (id, dir) => {
    if (readOnly) return;
    setRrPoolIds((prev) => {
      const i = prev.indexOf(id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const handleRrDrop = (targetId) => {
    const dragId = dragIdRef.current;
    dragIdRef.current = null;
    setDragOverId(null);
    if (readOnly || !dragId || dragId === targetId) return;
    setRrPoolIds((prev) => {
      const to = prev.indexOf(targetId);
      if (to < 0 || !prev.includes(dragId)) return prev;
      const next = prev.filter((x) => x !== dragId);
      next.splice(to, 0, dragId);
      return next;
    });
  };

  const handleTeamAccessChange = (checked) => {
    if (readOnly) return;
    setError("");
    setTeamAccess(checked);
    if (!checked) {
      setRoundRobin(false);
      setCompaniesToAdd([]);
    }
  };

  const handleDiscard = () => {
    setError("");
    setTeamAccess(saved.teamAccess);
    setRoundRobin(saved.roundRobin);
    setRrPoolIds(saved.rrPoolIds);
  };

  const handleSave = async () => {
    if (readOnly) return;
    setError("");
    const rrOn = teamAccess && roundRobin;
    if (rrOn && rrPoolIds.length === 0) {
      setError("Select at least one member in the round-robin pool before saving.");
      return;
    }
    setSaving(true);
    // Pool must exist before enabling round-robin (server validates the pool size).
    if (rrOn || rrPoolIds.length) {
      const poolRes = await dispatch(setJobRoundRobinPool(jobId, rrPoolIds));
      if (!poolRes.success) {
        setError(poolRes.message);
        setSaving(false);
        return;
      }
    }
    const res = await dispatch(updateJobAssignmentSettings(jobId, {
      team_access_enabled: teamAccess,
      round_robin_enabled: rrOn,
    }));
    setSaving(false);
    if (!res.success) {
      setError(res.message);
      return;
    }
    setRoundRobin(rrOn);
    setSaved({ teamAccess, roundRobin: rrOn, rrPoolIds });
    notify("Team & assignment settings saved", "success");
  };

  const changeHint = [
    teamAccess !== saved.teamAccess && "job team access",
    roundRobin !== saved.roundRobin && "round-robin",
    rrPoolIds.join(",") !== saved.rrPoolIds.join(",") && "rotation order",
  ].filter(Boolean).join(", ");

  const renderRrBody = () => {
    if (!teamAccess) {
      return <p className="ta-hint">Turn on job team access to share candidates out automatically.</p>;
    }
    if (!roundRobin) {
      return <p className="ta-hint">Turned off. New candidates stay unassigned until someone assigns them.</p>;
    }
    if (!team.length) {
      return <div className="ta-rr-empty">Add people to the job team to set up a rotation.</div>;
    }
    return (
      <>
        <ol className="ta-order">
          {orderedRrMembers.map((m) => {
            const idx = rrPoolIds.indexOf(m.id);
            const inPool = idx > -1;
            return (
              <li
                key={m.id}
                className={`${inPool ? "" : "is-out"}${dragOverId === m.id ? " is-over" : ""}`}
                draggable={inPool && !readOnly}
                onDragStart={() => { dragIdRef.current = m.id; }}
                onDragEnd={() => { dragIdRef.current = null; setDragOverId(null); }}
                onDragOver={(e) => { if (inPool && !readOnly) { e.preventDefault(); setDragOverId(m.id); } }}
                onDragLeave={() => setDragOverId((cur) => (cur === m.id ? null : cur))}
                onDrop={(e) => { e.preventDefault(); handleRrDrop(m.id); }}
              >
                <input
                  type="checkbox"
                  className="ta-inc"
                  checked={inPool}
                  disabled={readOnly}
                  aria-label={`Include ${m.name} in rotation`}
                  onChange={(e) => toggleRrMember(m.id, e.target.checked)}
                />
                <span className="ta-pos">{inPool ? idx + 1 : "–"}</span>
                <div className="ta-av ta-av-sm">{initialsOf(m.name)}</div>
                <div className="ta-nm">
                  <b>{m.name}</b>
                  <small>{m.role_name || "No role"}</small>
                </div>
                <div className="ta-arrows">
                  <button type="button" aria-label={`Move ${m.name} up`}
                    disabled={readOnly || !inPool || idx === 0} onClick={() => moveRr(m.id, -1)}>{Icon.up}</button>
                  <button type="button" aria-label={`Move ${m.name} down`}
                    disabled={readOnly || !inPool || idx === rrPoolIds.length - 1} onClick={() => moveRr(m.id, 1)}>{Icon.down}</button>
                </div>
              </li>
            );
          })}
        </ol>
        <p className="ta-hint">Tick the people to include, then drag or use the arrows to set the order.</p>
      </>
    );
  };

  return (
    <div ref={rootRef} className={`job-prescreening team-assignment${isDirty ? " has-savebar" : ""}`}>
      <div className="job-settings-head" style={{ alignItems: "center" }}>
        <img
          src="/images/back-arrow-black.svg"
          alt="back"
          width={32}
          height={32}
          onClick={() => setType("")}
          style={{ cursor: "pointer" }}
        />
        <div className="job-settings-head-title">
          <h3>Team &amp; Assignment</h3>
          <p style={{ margin: 0 }}>Choose who works on this job and how new candidates are shared out.</p>
        </div>
      </div>

      {loading ? (
        <div className="loading-overlay" style={{ position: "relative", marginTop: 16, alignItems: "start", height: "32px" }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          {error && <div className="ta-error" role="alert">{error}</div>}

          <div className="ta-grid">
            {/* LEFT: Job team */}
            <section className="ta-card" aria-labelledby="ta-team-h">
              <div className="ta-card-h">
                <div>
                  <h3 id="ta-team-h">
                    Job team <span className="ta-count">{team.length}</span>
                  </h3>
                  <p>Restrict this job to the people below. Admins always have access.</p>
                </div>
                <Switch checked={teamAccess} disabled={readOnly} onChange={handleTeamAccessChange} label="Job team access" />
              </div>

              {/* Access status on the left, add-member controls on the right */}
              <div className="ta-toolbar">
              {teamAccess ? (
                <div className="ta-status is-on">
                  {Icon.lock}
                  Only admins and the people below can see this job.
                </div>
              ) : (
                <div className="ta-status is-off">
                  {Icon.globe}
                  Everyone in your Team can see this job and its candidates.
                </div>
              )}

              <div className="ta-add">
                <Select
                  className="ta-add-select"
                  classNamePrefix="ta-add-select"
                  placeholder={
                    !teamAccess ? "Turn on job team access to add Teams"
                      : companyOptions.length ? "Search or pick companies…" : "No Teams available"
                  }
                  isDisabled={readOnly || !teamAccess || !companyOptions.length || addingCompanies}
                  isMulti
                  isClearable
                  closeMenuOnSelect={false}
                  options={companyOptions}
                  value={companyOptions.filter((o) => companiesToAdd.includes(o.value))}
                  onChange={(opts) => setCompaniesToAdd((opts || []).map((o) => o.value))}
                  formatOptionLabel={(o, { context }) =>
                    context === "value" ? o.company.name : (
                      <div className="ta-opt">
                        <div className="ta-opt-main">
                          <span className="ta-opt-name">{o.company.name}</span>
                        </div>
                        {o.company.code && <div className="ta-opt-sub">{o.company.code}</div>}
                      </div>
                    )
                  }
                  maxMenuHeight={240}
                  menuPortalTarget={document.body}
                  styles={addSelectStyles}
                  noOptionsMessage={() => "No Teams to add"}
                />
                <button
                  type="button"
                  className="ta-btn-outline"
                  disabled={readOnly || !teamAccess || !companiesToAdd.length || addingCompanies}
                  onClick={handleAddCompanies}
                >
                  {addingCompanies ? "Adding…" : "Add"}
                </button>
              </div>
              </div>

              <div className="ta-thead" role="row">
                <div>Member</div>
                <div className="ta-thead-access">
                  Candidate access
                  <InfoTip text={TEAM_ASSIGNMENT_TIPS.fullAccess} />
                </div>
                <div style={{ textAlign: "center" }}>Action</div>
              </div>

              {team.length === 0 ? (
                <div className="ta-empty">No one is on this job yet. Add a Team above to get started.</div>
              ) : (
                team.map((m) => (
                  <div
                    key={m.id}
                    className={`ta-row${justAddedId === m.id ? " is-new" : ""}`}
                    onAnimationEnd={() => justAddedId === m.id && setJustAddedId(null)}
                  >
                    <div className="ta-who">
                      <div className="ta-av">{initialsOf(m.name)}</div>
                      <div className="ta-meta">
                        <b>
                          {m.name}
                          <RoleBadge roleKey={m.role_key} label={m.role_name} />
                        </b>
                        {m.email && <small>{m.email}</small>}
                      </div>
                    </div>
                    <div className="ta-acc">
                      {/* Admins and managers always have full access, so there's nothing to toggle */}
                      {!hasFixedFullAccess(m.role_key) && (
                        <label className={`ta-check${m.full_candidate_access ? " is-on" : ""}${readOnly ? " is-disabled" : ""}`}>
                          <input
                            type="checkbox"
                            checked={m.full_candidate_access}
                            disabled={readOnly || busyMemberId === m.id}
                            onChange={(e) => handleToggleFullAccess(m, e.target.checked)}
                          />
                          Full candidate access
                        </label>
                      )}
                    </div>
                    <div className="ta-act">
                      <button
                        type="button"
                        className="ta-del"
                        disabled={readOnly || busyMemberId === m.id}
                        aria-label={`Remove ${m.name} from team`}
                        title={readOnly ? "You have view-only access to job settings" : "Remove from team"}
                        onClick={() => !readOnly && setRemoveConfirm(m)}
                      >
                        {Icon.trash}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </section>

            {/* RIGHT: round robin + permissions */}
            <aside className="ta-side">
              <section className={`ta-card${teamAccess ? "" : " is-muted"}`} aria-labelledby="ta-rr-h">
                <div className="ta-card-h">
                  <div>
                    <h3 id="ta-rr-h">
                      Round-robin assignment
                      <InfoTip text={TEAM_ASSIGNMENT_TIPS.roundRobin} />
                    </h3>
                    <p>New candidates are assigned to people in this order, one at a time.</p>
                  </div>
                  <Switch
                    checked={teamAccess && roundRobin}
                    disabled={readOnly || !teamAccess}
                    onChange={(checked) => { if (readOnly) return; setError(""); setRoundRobin(checked); }}
                    label="Round-robin assignment"
                  />
                </div>
                {renderRrBody()}
              </section>

              {/* <section className="ta-card">
                <button
                  type="button"
                  className="ta-perm-toggle"
                  aria-expanded={permOpen}
                  aria-controls="ta-perm"
                  onClick={() => setPermOpen((o) => !o)}
                >
                  <div>
                    <h3>Who can do what</h3>
                    <p>Permissions for each type of team member</p>
                  </div>
                  {Icon.chevron}
                </button>
                {permOpen && (
                  <div className="ta-perm" id="ta-perm">
                    <table>
                      <thead>
                        <tr>
                          <th />
                          {PERMISSION_COLUMNS.map((c) => <th key={c}>{c}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {PERMISSION_ROWS.map((r) => (
                          <tr key={r.label}>
                            <td>{r.label}</td>
                            {r.values.map((v, i) => (
                              <td key={i}><span className={PERMISSION_MARK[v].cls}>{PERMISSION_MARK[v].text}</span></td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="ta-legend">
                      <span><i className="ta-yes">✓</i>Allowed</span>
                      <span><i className="ta-part">◐</i>Own candidates only</span>
                      <span><i className="ta-no">✕</i>Not allowed</span>
                    </div>
                    <p className="ta-foot">
                      Applies while job team access is on. “Full candidate access” and “Assigned only” are recruiters with
                      the checkbox ticked or unticked. Candidates assigned to others appear greyed out for “Assigned only” members.
                    </p>
                  </div>
                )}
              </section> */}
            </aside>
          </div>

          <div
            className={`ta-savebar${isDirty ? " is-shown" : ""}`}
            style={barPos ? { left: barPos.left, width: barPos.width } : undefined}
            role="region"
            aria-label="Unsaved changes"
          >
            <div>
              <span className="ta-dot" />
              <b>Unsaved changes</b>
              {changeHint && <span className="ta-change-hint"> {changeHint}</span>}
            </div>
            <div className="ta-savebar-actions">
              <button type="button" className="ta-btn-ghost" disabled={saving} onClick={handleDiscard}>Discard</button>
              <button type="button" className="ta-btn-dark" disabled={saving} onClick={handleSave}>
                {saving ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        </>
      )}

      <Modal show={!!removeConfirm} centered onHide={() => setRemoveConfirm(null)} dialogClassName="detail-modal">
        <Modal.Body style={{ padding: "24px" }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 10 }}>Remove from job team?</h3>
          <p style={{ fontSize: 14, color: "#374151", marginBottom: 20 }}>
            <strong>{removeConfirm?.name}</strong> will lose access to this job. Candidates assigned to them may be
            unassigned and they will be removed from the round-robin pool.
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button className="ta-modal-cancel" onClick={() => setRemoveConfirm(null)}>Cancel</button>
            <button className="ta-modal-confirm" onClick={handleRemoveMember}>Remove</button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default TeamAssignment;
